import OrderInfoWindow from "./maps/order_info_window";
/**
 * GoogleMap hook
 *
 * Hook to be used to render a google map into an element on the page. This hook
 * supports both a declarative API with `data-mapdata` attributes on a DOM element and
 * an imperative API with phx hooks `handleEvent` (`data-event`).
 *
 * It is also possible to use both modes so you can bootstrap the map with a
 * data prop and then receive updates over the socket after that.
 *
 * The following options are supported in a `data-mapdata` json encoded string on the map DOM element.
 *
 *    - center (object) - lat/lng object to center the map
 *    - zoom (int) - initial zoom level
 *    - bounds (object) - lat/lng bounds object
 *    - padding (int/object) - padding to use when rendering bounds.
 *    - geojson (object) - geojson feature collection for the map data layer
 *    - events: (array) - array of event types to send events on
 *
 * ## Data Types
 *
 * ### center
 *
 * Where to center the map.
 *
 * Ex:
 *
 *    {"lat": 39.777592, "lng": -86.1577709}
 *
 * ### bounds
 *
 * Object with `north`, `south`, `east` and `west` properties.
 *
 * Alternatively, * bounds can be set to the string "auto" which will case the
 * map to fit the to the bounds of the elements rendered on the map.
 *
 * Ex:
 *
 *     {
 *       "north": 39.777592,
 *       "south" : 39.277592,
 *       "east": -86.1577709,
 *       "west" : -86.2577709
 *     }
 *
 *
 * ### padding
 *
 * Can be a single padding, `padding: 10`, which will add padding of 10px to all
 * four sides of the map when adjusting bounds. Or can be an object `{top: 30,
 * left: 10} which will apply a top padding of 30px and a left padding of 10px.
 *
 * ex:
 *
 *    # Single
 *    padding: 10
 *
 *    # Object
 *    padding: {top: 30, left: 10}
 *
 * ### geojosn
 *
 * This is the supported way of rendering anything on the map. It
 * utilizes the google maps data layer which converts geojson types into map
 * elements.
 *
 * For example, a GeoJson `Point` gets rendered as a Marker on the
 * map and a `Polygon` gets rendered as a Polygon on the map, etc.
 *
 * To render multiple features, you encode them into a `FeatureCollection`.
 *
 * see: https://developers.google.com/maps/documentation/javascript/datalayer
 *
 * Ex:
 *
 *     {
 *       "type": "Feature",
 *       "geometry": {
 *          "coordinates": [
 *            -86.1577709,
 *            39.777592
 *          ],
 *          "type": "Point"
 *        },
 *        "properties": {
 *           "events": [
 *             "click"
 *           ],
 *           "id": "01GJ54ER3M2AS3KEYVW5K63CWB",
 *           "type": "Store"
 *        }
 *     }
 *
 *
 * ### Events
 *
 * Register for events on map elements to be sent to the server. In order for a
 * map element to sent up an event it *must* have a `id` and a `type` property.
 *
 * See: https://developers.google.com/maps/documentation/javascript/reference/data#Data.Feature
 *
 * Ex:
 *
 *    ["click", "mouseover"]
 *
 *
 * ## Pushing updates from the server
 *
 * Updates can be pushed using `push_event/3` inside of your LV.

      def handle_event("event_that_change_map_data", data, socket) do
        {:noreply, push_event(socket, "google-map-update", %{center: %{lat: 10, lng: 10}})}
      end

 * Any valid `mapdata` can be pushed and the map will be updated to reflect the new data.
 *
 * ## Removing features from the server
 *
 * Map features can be removed using `push_event/3` inside of your LV.

      def handle_event("event_that_remove_map_data", data, socket) do
        {:noreply, push_event(socket, "google-map-remove-features", %{ids: ["12345", "67890"]})}
      end

 * The `ids` key can contain a single string ID or a list of string IDs. These IDs should
 * correspond to the ID of a feature that has been added to the map; if it's found, it
 * will be removed. If it is not, it will silently be ignored.
 *
 *
 * ## Moving markers from the server
 *
 * Map markers can be moved around in an animated fashion from the server with a
 * `push_event/3` from the LV. Google maps provides no way to smoothly animate a
 * marker from point A to B, so this is different than just updating the coords
 * of marker. If a Marker was moved that does not exist, then the Feature will be
 * added to the map so future calls can move that Marker.

      push_event(socket, "google-map-move-feature", %{feature: %{type: "Point", coordinates: [new, coords]}})

 * ## Handling events from the map
 *
 * Each element rendered on the map can send updates back to your LV which can
 * be handled. See "events" above for the requirements.
 *
 *
 * Updates are sent back to the LV under the `google-map-event` key. The payload
 * will contain a `type` which is the type of event that happened (eg: "click"
 * or "mouseover") and a `data` object which contains what element received the
 * event.
 *
 *
 *     @impl true
 *     def handle_event("google-map-event", _payload, socket) do
 *       {:noreply, socket}
 *     end

 *     [debug] HANDLE EVENT
 *       View: ScratchWeb.StoresLive.Show
 *       Event: "google-map-event"
 *       Parameters: %{"event" => "click", "data" => %{"id" => "01GJC1CZHN9NV3KVYBTM99A3J1", "type" => "Driver"}}
 *
 * ## Note about map updates
 *
 *  If you are using `data-mapdata` and `data-event` you will want to wrap your
 *  google map `div` with another `div` that has `phx-update="ignore"`. This
 *  will prevent the DOM element that contains the google map from being removed
 *  and rebuilt completely.
 *
 *  This ultimately prevents the map from disappearing and coming back.
 *
      <div id="google-map-wrapper" phx-update="ignore">
        <div
          class="mt-6 h-80"
          id="google-map"
          phx-hook="GoogleMap"
          data-mapdata={google_map_data(@order)}
          data-event="map_update"
        >
        </div>
      </div>
 *
 *
 * ## Examples
 *
    Basic map centered at a location

      <div
        class="mt-6 h-80"
        id="google-map"
        phx-hook="GoogleMap"
        data-mapdata={~s(
          {
            "zoom": 10,
            "center": {
              "lat": 39.777592,
              "lng": -86.1555822
            }
      }
    )}></div>

    Map with a Marker that will send click events to the server

        <div id="google-map-wrapper" phx-update="ignore">
          <div
            class="h-96"
            id="google-map"
            phx-hook="GoogleMap"
            data-event="map_update"
            data-mapdata={
              Jason.encode!(%{
                center: %{lat: 39.777592, lng: -86.1577709},
                events: ["click"],
                geojson:
                  %Geo.Point{
                    coordinates: {-86.1577709, 39.777592},
                    properties: %{
                      type: "Driver",
                      id: "uuid",
                      # icon: "https://maps.google.com/mapfiles/ms/icons/blue-dot.png"
                    }
                  }
                  |> Geo.JSON.encode!(feature: true),
                zoom: 13
              })
            }
          >
          </div>
        </div>

    Map with multiple features

        <div id="google-map-wrapper" phx-update="ignore">
          <div
            class="h-96"
            id="google-map"
            phx-hook="GoogleMap"
            data-event="map_update"
            data-mapdata={
              Jason.encode!(%{
                center: %{lat: 39.777592, lng: -86.1577709},
                events: ["mouseover", "click"],
                bounds: "auto",
                geojson:
                  %Geo.GeometryCollection{
                    geometries: [
                      %Geo.Point{
                        coordinates: {-86.1577709, 39.777592},
                        properties: %{
                          type: "Driver",
                          id: Ecto.UUID.generate()
                        }
                      },
                      %Geo.Polygon{
                        coordinates: [
                          [
                            {-86.1577709, 39.777592},
                            {-86.2577709, 39.977592},
                            {-86.4577709, 39.987592},
                            {-86.1577709, 39.777592}
                          ]
                        ],
                        properties: %{
                          strokeColor: "red",
                          fillColor: "red"
                        }
                      }
                    ]
                  }
                  |> Geo.JSON.encode!(feature: true),
                zoom: 13
              })
            }
          >
          </div>
        </div>

 */
const GoogleMapHook = {
  infoWindow: new OrderInfoWindow(),
  mounted() {
    const eventCallback = (event, data) => {
      this.pushEvent("google-map-event", { event, data });
    };

    this.initMap(eventCallback);

    this.handleEvent("google-map-update", (data) => {
      if (!this.map) {
        return;
      }

      this.clearMap = renderMap(this.map, data, eventCallback);
      this.infoWindow.receivedUpdateEvent({ data, map: this.map });
    });

    this.handleEvent("google-map-pan", (data) => {
      if (data.zoom) {
        this.map.setZoom(data.zoom);
      }
      this.map.panTo(data.center);
    });

    this.handleEvent("google-map-auto-bounds", (data) => {
      fitBounds(this.map, data, true);
    });

    this.handleEvent("google-map-move-feature", (data) => {
      if (!this.map || !this.map.data) {
        return;
      }

      if (data?.feature?.properties?.id) {
        const feature = data.feature;
        const mapFeature = this.map.data.getFeatureById(
          data.feature.properties.id,
        );
        if (!mapFeature) {
          this.map.data.addGeoJson(data.feature, { idPropertyName: "id" });
          return;
        }

        const featureGeom = mapFeature.getGeometry();
        const currentLatLng = featureGeom.get();
        const currentLat = currentLatLng.lat();
        const currentLng = currentLatLng.lng();
        const [destLng, destLat] = feature.geometry.coordinates;

        latlngs = [];
        for (var percent = 0; percent <= 1; percent += 0.1) {
          curLat = currentLat + percent * (destLat - currentLat);
          curLng = currentLng + percent * (destLng - currentLng);
          latlngs.push(new google.maps.LatLng(curLat, curLng));
        }

        animateMarkerMove(mapFeature, latlngs, 0, 20);
      }
    });

    this.handleEvent("google-map-remove-features", (data) => {
      if (!this.map || !this.map.data) {
        return;
      }

      let ids = [];
      if (Array.isArray(data.ids)) {
        ids = data.ids;
      } else if (typeof data.ids == "string") {
        ids = [data.ids];
      }

      ids.forEach((id) => {
        const feature = this.map.data.getFeatureById(id);
        if (!feature) {
          return;
        }

        this.map.data.remove(feature);
        this.infoWindow.receivedRemoveFeaturesEvent({ feature });
      });
    });
  },

  updated() {
    const eventCallback = (event, data) => {
      this.pushEvent("google-map-event", { event, data });
    };
    this.initMap(eventCallback);
  },

  initMap(eventCallback) {
    // if the google maps API has not loaded yet, set up a listener and bail out
    if (!window.google) {
      window.addEventListener("google_map_loaded", (e) => {
        this.updated();
      });
      return;
    }

    let data = {
      zoom: 10,
      streetViewControl: false,
      mapTypeControl: false,
      fullscreenControl: false,
      gestureHandling: "cooperative",
      events: [],
    };

    if (this.el.dataset.mapdata) {
      try {
        const parsed_data = JSON.parse(this.el.dataset.mapdata);
        data = {
          ...data,
          ...parsed_data,
        };
      } catch {}
    }

    this.map = newMap(this.el, data);
    this.clearMap = renderMap(this.map, data, eventCallback);
  },
};

const newMap = (el, data) => {
  return new google.maps.Map(el, {
    zoom: data.zoom,
    center: data.center,
    gestureHandling: data.gestureHandling,
    streetViewControl: data.streetViewControl,
    mapTypeControl: data.mapTypeControl,
    fullscreenControl: data.fullscreenControl,
    keyboardShortcuts: false,
    // doc: https://developers.google.com/maps/documentation/javascript/examples/hiding-features
    styles: [
      {
        featureType: "poi",
        stylers: [{ visibility: "off" }],
      },
      {
        featureType: "administrative",
        stylers: [{ visibility: "off" }],
      },
      {
        featureType: "transit",
        elementType: "labels.icon",
        stylers: [{ visibility: "off" }],
      },
    ],
  });
};

const animateMarkerMove = (marker, latlngs, index, wait) => {
  marker.setGeometry(latlngs[index]);
  if (index != latlngs.length - 1) {
    setTimeout(() => {
      animateMarkerMove(marker, latlngs, index + 1, wait);
    }, wait);
  }
};

const addFeatureListener = (map, event, eventCallback) => {
  map.data.addListener(event, (e) => {
    const feature = e.feature;

    if (feature) {
      const featureId = feature.getProperty("id");
      const featureType = feature.getProperty("type");

      if (featureId && featureType && eventCallback) {
        eventCallback(event, { id: featureId, type: featureType });
      }
    }
  });
};

const fitBounds = (map, data, autobounds) => {
  if (autobounds) {
    const mapBounds = new google.maps.LatLngBounds();
    map.data.forEach((feature) => {
      const geometry = feature.getGeometry();
      if (geometry) {
        processPoints(geometry, mapBounds);
      }
    });
    map.fitBounds(mapBounds, data.padding || 0);
  } else {
    if (data.center) {
      map.panTo(data.center);
    }
  }
};

const renderMap = (map, data, eventCallback) => {
  const autobounds = data.bounds === "auto";
  if (data.clear_features) {
    map.data.forEach((feature) => {
      map.data.remove(feature);
    });
  }

  if (data.geojson) {
    map.data.addGeoJson(data.geojson, { idPropertyName: "id" });

    fitBounds(map, data, autobounds);

    if (data.events) {
      data.events.forEach((event) => {
        addFeatureListener(map, event, eventCallback);
      });
    }

    map.data.setStyle((feature) => {
      const style = {};
      feature.forEachProperty((v, p) => {
        style[p] = v;
      });
      return style;
    });
  }

  if (!autobounds && data.zoom) {
    map.setZoom(data.zoom);
  }

  const clearMap = () => {
    // clear the data layer
    map.data.forEach((feature) => {
      map.data.remove(feature);
    });

    // clear all listeners on the data layer
    google.maps.event.clearInstanceListeners(map.data);
  };

  return clearMap;
};

const processPoints = (geometry, bounds) => {
  if (geometry instanceof google.maps.LatLng) {
    bounds.extend(geometry);
  } else if (geometry instanceof google.maps.Data.Point) {
    bounds.extend(geometry.get());
  } else {
    geometry.getArray().forEach((g) => {
      processPoints(g, bounds);
    });
  }
};

export default GoogleMapHook;
